"use client";

import { SiteLink } from "@i18n/SiteLink";
import type { PlanDisplayData } from "@saas/payments/hooks/plan-data";
import {
	type BillingInterval,
	type V2PlanCard,
	formatUsd,
	getV2PlanCards,
} from "@saas/payments/lib/pricing-v2";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	ArrowRightIcon,
	CheckIcon,
	CoinsIcon,
	PhoneIcon,
	StarIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

export interface PlanCardAction {
	label: string;
	onClick?: () => void;
	disabled?: boolean;
	loading?: boolean;
}

const ENTERPRISE_FEATURES = [
	"customVolume",
	"security",
	"pinnedModels",
	"morePlatforms",
] as const;

const ALL_PLANS_INCLUDE = [
	"unlimitedUsers",
	"unlimitedBrands",
	"sso",
	"mcpApi",
	"locationTracking",
] as const;

/**
 * The v2 plan cards: Brand, Agency Core (most popular), Agency Pro and
 * Enterprise, then the "Every plan includes" strip. Prices come from the plan
 * config, allowance rows and bullets from `usePlanData` (built on the
 * entitlements table). No platform names on the cards: the pricing FAQ has
 * the platform list. Grid columns follow the container, so the same cards
 * fit the marketing page and the narrower billing settings.
 */
export function PlanCardsV2({
	interval,
	planData,
	actionFor,
	legacyPrompts,
	className,
}: {
	interval: BillingInterval;
	planData: Record<string, PlanDisplayData>;
	actionFor: (card: V2PlanCard) => PlanCardAction;
	/** In-app, for a legacy subscriber: their plan's prompts, to show the gain. */
	legacyPrompts?: number;
	className?: string;
}) {
	return (
		<div className={cn("@container", className)}>
			<div className="grid gap-5 @2xl:grid-cols-2 @5xl:grid-cols-4">
				{getV2PlanCards().map((card) => (
					<PlanCard
						key={card.key}
						card={card}
						data={planData[card.key]}
						interval={interval}
						action={actionFor(card)}
						legacyPrompts={legacyPrompts}
					/>
				))}
				<EnterpriseCard />
			</div>
			<AllPlansInclude className="mt-6" />
		</div>
	);
}

function PlanCard({
	card,
	data,
	interval,
	action,
	legacyPrompts,
}: {
	card: V2PlanCard;
	data: PlanDisplayData | undefined;
	interval: BillingInterval;
	action: PlanCardAction;
	legacyPrompts?: number;
}) {
	const t = useTranslations();
	const price = card.prices[interval] ?? card.prices.month;
	const extraPrompts =
		legacyPrompts === undefined ? 0 : card.prompts - legacyPrompts;

	return (
		<div
			className={cn(
				"relative flex flex-col rounded-2xl border bg-card p-6",
				card.recommended &&
					"border-primary shadow-md ring-1 ring-primary",
			)}
			data-test="price-table-plan"
		>
			{card.recommended ? (
				<span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
					<StarIcon className="size-3" aria-hidden />
					{t("pricing.v2.mostPopular")}
				</span>
			) : null}

			<h3 className="text-lg font-bold tracking-tight">{data?.title}</h3>
			<p className="mt-1 text-sm text-muted-foreground">
				{data?.description}
			</p>

			{price ? (
				<div className="mt-5">
					<p className="flex items-baseline gap-1">
						<span
							className="text-4xl font-extrabold tracking-tight"
							data-test="price-table-plan-price"
						>
							{formatUsd(price.perMonth)}
						</span>
						<span className="text-sm text-muted-foreground">
							{t("pricing.v2.perMonth")}
						</span>
					</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{interval === "month"
							? t("pricing.v2.billedMonthly")
							: interval === "quarter"
								? t("pricing.v2.billedQuarterly", {
										amount: formatUsd(price.billed),
									})
								: t("pricing.v2.billedYearly", {
										amount: formatUsd(price.billed),
									})}
					</p>
				</div>
			) : null}

			{extraPrompts > 0 ? (
				<p className="mt-4 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
					{t.rich("pricing.v2.morePrompts", {
						count: extraPrompts.toLocaleString("en-US"),
						strong: (chunks) => (
							<span className="font-semibold text-foreground">
								{chunks}
							</span>
						),
					})}
				</p>
			) : null}

			<Button
				variant={card.recommended ? "primary" : "outline"}
				className="mt-5 w-full"
				onClick={action.onClick}
				disabled={action.disabled}
				loading={action.loading}
			>
				{action.label}
				{action.disabled ? null : (
					<ArrowRightIcon className="ml-1.5 size-4" aria-hidden />
				)}
			</Button>

			<ul className="mt-6 space-y-2.5 text-sm">
				{(data?.allowances ?? []).map((item, i) => (
					<CardItem
						key={i}
						icon={i === 1 ? "coins" : "check"}
						strong={i === 0}
					>
						{item}
					</CardItem>
				))}
			</ul>

			{data?.outcomeFeatures?.length ? (
				<div className="mt-6 border-t pt-5">
					<p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
						{data.featuresHeading}
					</p>
					<ul className="mt-3 space-y-2.5 text-sm">
						{data.outcomeFeatures.map((f, i) => (
							<CardItem key={i}>{f}</CardItem>
						))}
					</ul>
				</div>
			) : null}
		</div>
	);
}

function EnterpriseCard() {
	const t = useTranslations();
	return (
		<div className="flex flex-col rounded-2xl border bg-muted/40 p-6">
			<h3 className="text-lg font-bold tracking-tight">
				{t("pricing.products.enterprise.title")}
			</h3>
			<p className="mt-1 text-sm text-muted-foreground">
				{t("pricing.v2.enterprise.audience")}
			</p>
			<div className="mt-5">
				<p className="text-4xl font-extrabold tracking-tight">
					{t("pricing.v2.enterprise.price")}
				</p>
				<p className="mt-1 text-xs text-muted-foreground">
					{t("pricing.v2.enterprise.priceNote")}
				</p>
			</div>
			<Button variant="outline" className="mt-5 w-full" asChild>
				<SiteLink href="/contact">
					<PhoneIcon className="mr-1.5 size-4" aria-hidden />
					{t("pricing.v2.enterprise.cta")}
				</SiteLink>
			</Button>
			<ul className="mt-6 space-y-2.5 text-sm">
				{ENTERPRISE_FEATURES.map((key) => (
					<CardItem key={key}>
						{t(`pricing.v2.enterprise.features.${key}`)}
					</CardItem>
				))}
			</ul>
		</div>
	);
}

function CardItem({
	children,
	icon = "check",
	strong,
}: {
	children: ReactNode;
	icon?: "check" | "coins";
	strong?: boolean;
}) {
	const Icon = icon === "coins" ? CoinsIcon : CheckIcon;
	return (
		<li className="flex items-start gap-2">
			<Icon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
			<span className={cn(strong && "font-semibold")}>{children}</span>
		</li>
	);
}

function AllPlansInclude({ className }: { className?: string }) {
	const t = useTranslations();
	return (
		<div
			id="all-plans-include"
			className={cn(
				"scroll-mt-24 rounded-2xl border bg-muted/40 px-6 py-5",
				className,
			)}
		>
			<p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
				{t("pricing.v2.allPlansInclude.title")}
			</p>
			<ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
				{ALL_PLANS_INCLUDE.map((key) => (
					<li key={key} className="flex items-center gap-1.5">
						<CheckIcon
							className="size-4 shrink-0 text-primary"
							aria-hidden
						/>
						{t(`pricing.v2.allPlansInclude.items.${key}`)}
					</li>
				))}
			</ul>
		</div>
	);
}
